import Container from "../../shared-components/Container";
import FormTwoColumnContainer from "../../shared-components/FormTwoColumnContainer";
import InputField from "../../shared-components/InputField";
import PageHeader from "../../shared-components/PageHeader";
import Toggle from "../../shared-components/Toggle";
import LabeledCheckbox from "../../shared-components/LabeledCheckbox";
import ErrorMessageContainer from "../../shared-components/ErrorMessageContainer";

import {
  useGetBehaviorKit,
  useGetBehaviorsByBehaviorKitId,
  useGetCompanyEx,
  useGoToCompany,
  useUpdateBehaviorKit,
} from "../../api/CompaniesApi";
import { useCallback, useEffect, useState } from "react";
import { BehaviorKitUpdate } from "../../api/Companies";
import Button from "../../shared-components/Button";
import { useLocation, useParams } from "react-router-dom";
import KeywordSummary from "../KeywordGroups/KeywordSummary";
import LoadingSpinner from "../../shared-components/LoadingSpinner";

import {
  ChevronLeftIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { orderBy } from "lodash";
import { getBehaviors } from "../../utils";
import DropdownCombo, {
  DropdownItem,
} from "../../shared-components/DropdownCombo";
import { behaviorMap, useFamaFlags } from "../../api/FamaFlags";
import { usePermissionValidator } from "../../api/UserPermissionValidators";
import LensTopicsSection from "../LensTopics/LensTopicsSection";

type errorState = {
  lookback: boolean;
};

type BehaviorKitFormState = {
  parentCompanyUUID?: string;
};

const BehaviorKitForm = () => {
  const { goToCompany, goToSubCompany } = useGoToCompany();
  const { companies, subcompanies } = usePermissionValidator();

  const state = useLocation().state as BehaviorKitFormState;
  const parentCompanyUUID = state?.parentCompanyUUID;

  const { behaviorKitId: behaviorKitUUID, companyId: companyUUID } =
    useParams();
  const {
    isLoading: isLoadingBehaviorKit,
    data: baseBehaviorKit,
    isSuccess: successLoadingBehaviorKit,
    mutate: loadBehaviorKit,
  } = useGetBehaviorKit();
  const {
    mutate: updateBehaviorKit,
    isSuccess: successUpdating,
    isLoading: isLoadingUpdate,
  } = useUpdateBehaviorKit();

  const {
    mutate: getCompanyEx,
    isLoading: loadingGetCompanyEx,
    data: companyEx,
  } = useGetCompanyEx();

  const {
    mutate: loadBehaviors,
    isSuccess: successBehaviors,
    isLoading: loadingBehaviors,
    data: behaviors,
  } = useGetBehaviorsByBehaviorKitId();
  const [companyMismatch, setCompanyMismatch] = useState<Boolean>(false);
  const [readOnly, setReadOnly] = useState<boolean>(true);

  const navigateToCompanyEdit = useCallback(() => {
    if (parentCompanyUUID) {
      goToSubCompany(parentCompanyUUID, companyUUID ?? "", "behavior-kits");
    } else {
      goToCompany(companyUUID ?? "", "behavior-kits");
    }
  }, [companyUUID, parentCompanyUUID, goToCompany, goToSubCompany]);

  useEffect(() => {
    if (companyUUID) {
      getCompanyEx(companyUUID);
    }
  }, [companyUUID, getCompanyEx]);

  useEffect(() => {
    if (companyEx?.parent_company_id && subcompanies.canEditBehaviorKits) {
      setReadOnly(false);
    }

    if (!companyEx?.parent_company_id && companies.canEditBehaviorKits) {
      setReadOnly(false);
    }
  }, [
    companyEx,
    companies.canEditBehaviorKits,
    subcompanies.canEditBehaviorKits,
  ]);

  const { behaviorMappingActions } = useFamaFlags();
  const [selectedBehaviors] = useState<[string, string][]>(getBehaviors());
  const [showLookBack, setShowLookBack] = useState<boolean>(false);
  const [error, setError] = useState<errorState>({ lookback: false });

  const [behaviorKit, setBehaviorKit] = useState<BehaviorKitUpdate>({
    company_uuid: companyUUID || "",
    name: "",
    lookback: 1,
    display_images: false,
    behaviors: behaviors?.map((b) => b.name) || [],
  });

  const lookbackOptions: DropdownItem[] = [
    { id: "0", label: "Use Lookback from Check Type" },
    { id: "1", label: "Customize" },
  ];

  const handleLookbackOption = (option: DropdownItem) => {
    setShowLookBack(option.id !== "0");
  };

  const showError = () => {
    return !!error.lookback;
  };

  const handleSave = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // check validations
    const newError: errorState = {
      lookback: showLookBack && (behaviorKit?.lookback ?? 0) < 1,
    };
    setError(newError);

    // save
    if (behaviorKitUUID && !newError.lookback) {
      if (!showLookBack) {
        behaviorKit.lookback = undefined;
      }
      updateBehaviorKit(behaviorKit);
    }
    e.preventDefault();
  };

  useEffect(() => {
    if (!successLoadingBehaviorKit && behaviorKitUUID) {
      loadBehaviorKit(behaviorKitUUID);
    }
  }, [behaviorKitUUID, loadBehaviorKit, successLoadingBehaviorKit]);

  useEffect(() => {
    if (
      successLoadingBehaviorKit &&
      behaviorKit &&
      behaviorKit.company_uuid !== companyUUID
    ) {
      setCompanyMismatch(true);
    }
  }, [successLoadingBehaviorKit, behaviorKit, companyUUID]);

  useEffect(() => {
    if (baseBehaviorKit?.external_id && successLoadingBehaviorKit) {
      loadBehaviors(baseBehaviorKit.external_id);
    }
  }, [baseBehaviorKit, loadBehaviors, successLoadingBehaviorKit]);

  useEffect(() => {
    if (baseBehaviorKit) {
      setBehaviorKit({
        ...behaviorKit,
        ...baseBehaviorKit,
        ...{ lookback: baseBehaviorKit.lookback ?? 1 },
      });
    }
    setShowLookBack(!!baseBehaviorKit?.lookback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseBehaviorKit, setBehaviorKit]);

  useEffect(() => {
    if (behaviors) {
      setBehaviorKit({
        ...behaviorKit,
        behaviors: orderBy(
          behaviors.map((b) => b.name),
          (n) => n.toLocaleLowerCase(),
          "asc"
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [behaviors, setBehaviorKit]);

  useEffect(() => {
    if (successUpdating) {
      navigateToCompanyEdit();
    }
  }, [successUpdating, navigateToCompanyEdit]);

  if (
    (isLoadingBehaviorKit || !behaviorKit.external_id || loadingBehaviors) &&
    behaviorKitUUID
  ) {
    return <LoadingSpinner />;
  }

  const isLoadingMutate = isLoadingUpdate;

  const hasLensTopicsEnabled = !!companyEx?.lens_topics;

  return (
    <>
      <PageHeader
        title={
          <>
            <div
              className="flex gap-3 text-sm font-thin text-gray-700 mb-3 items-center cursor-pointer"
              onClick={navigateToCompanyEdit}
            >
              <ChevronLeftIcon className="w-3" />
              <span>Back</span>
            </div>
            <>Behavior Kit</>
          </>
        }
      />

      <Container>
        {companyMismatch && (
          <ErrorMessageContainer message="Error loading Behavior Kit" />
        )}
        {!companyMismatch && (
          <form>
            <FormTwoColumnContainer title="Behavior Kit Name">
              <div className="w-96">
                <InputField
                  handleChange={(e) => {
                    setBehaviorKit({ ...behaviorKit, name: e.target.value });
                  }}
                  name={"behavior-kit-name"}
                  placeholder="Type here..."
                  classes="border-rose-600"
                  value={behaviorKit.name}
                  disabled={readOnly}
                />
              </div>
            </FormTwoColumnContainer>
            <FormTwoColumnContainer className="my-16" title="Settings">
              <div className="w-96">
                <Toggle
                  label={"Show images on finished report"}
                  onChange={(value) =>
                    setBehaviorKit({ ...behaviorKit, display_images: value })
                  }
                  defaultValue={behaviorKit.display_images}
                  disabled={readOnly}
                />
              </div>
            </FormTwoColumnContainer>
            <FormTwoColumnContainer className={"my-16"} title="Behavior Flags">
              {(successBehaviors || !behaviorKitUUID) && (
                <div className="flex gap-4 p-4 flex-col border-2 rounded-lg w-96">
                  <LabeledCheckbox
                    label={"Select All"}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const isChecked = e.target.checked;
                      let updatedBehaviors: string[] = [];

                      if (isChecked) {
                        updatedBehaviors = selectedBehaviors.map((bk) => bk[0]);
                      }

                      setBehaviorKit({
                        ...behaviorKit,
                        behaviors: updatedBehaviors,
                      });
                    }}
                    name={"Select All"}
                    disabled={readOnly}
                  />
                  {selectedBehaviors.map((item) => (
                    <div key={item[0]} className="capitalize font-normal my-1">
                      <LabeledCheckbox
                        checked={
                          !!behaviorKit.behaviors.find((b) => b === item[0])
                        }
                        name={item[0]}
                        toolTip={item[1]}
                        label={behaviorMap(
                          item[0],
                          behaviorMappingActions,
                          "key"
                        )}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const _selectedBehaviors = [...behaviorKit.behaviors];
                          if (!e.target.checked) {
                            const index = _selectedBehaviors.indexOf(
                              e.target.name
                            );
                            if (index > -1) {
                              // only splice array when item is found
                              _selectedBehaviors.splice(index, 1); // 2nd parameter means remove one item only
                            }
                          } else {
                            _selectedBehaviors.push(e.target.name);
                          }
                          setBehaviorKit({
                            ...behaviorKit,
                            behaviors: _selectedBehaviors,
                          });
                        }}
                        disabled={readOnly}
                      />
                    </div>
                  ))}
                </div>
              )}
            </FormTwoColumnContainer>
            <FormTwoColumnContainer title="Lookback">
              <DropdownCombo
                items={lookbackOptions}
                onChange={(e) => {
                  handleLookbackOption(e);
                }}
                initial={showLookBack ? lookbackOptions[1] : lookbackOptions[0]}
                html={true}
                name="behavior-kit-lookback-calculation"
                icon={
                  <InformationCircleIcon height={24} width={24} color="grey" />
                }
                disabled={readOnly}
              ></DropdownCombo>
              {showLookBack && (
                <div className="mt-4 mb-6">
                  <InputField
                    value={behaviorKit.lookback ?? 1}
                    type={"number"}
                    name={"look-back-period"}
                    placeholder=""
                    label={"Lookback period (months)"}
                    labelClassName="mb-0 w-80"
                    classes="flex items-center"
                    min={1}
                    className="w-16"
                    handleChange={(e) => {
                      setBehaviorKit({
                        ...behaviorKit,
                        lookback: parseInt(e.target.value),
                      });
                    }}
                  />
                </div>
              )}
            </FormTwoColumnContainer>
            {behaviorKitUUID && !readOnly && (
              <FormTwoColumnContainer
                className={"my-16"}
                title="Keyword Groups"
              >
                <KeywordSummary behaviorKitUUID={behaviorKit.external_id} />
              </FormTwoColumnContainer>
            )}
            {behaviorKitUUID && !readOnly && hasLensTopicsEnabled && (
              <FormTwoColumnContainer className={"my-16"} title="Lens - Topics">
                <LensTopicsSection behaviorKitUUID={behaviorKitUUID} />
              </FormTwoColumnContainer>
            )}
            {isLoadingMutate && <LoadingSpinner />}
            {showError() && (
              <div className="p-4 my-4 bg-red-300 text-red-900 rounded-lg">
                <ul>
                  {error.lookback && (
                    <li>Custom Lookback cannot be less than 1.</li>
                  )}
                </ul>
              </div>
            )}
            {!isLoadingMutate && !readOnly && !loadingGetCompanyEx && (
              <div className="flex gap-4 pb-4">
                <Button text="Save" onClick={handleSave} />
              </div>
            )}
          </form>
        )}
      </Container>
    </>
  );
};

export default BehaviorKitForm;
